/**
 * The session, as the API reports it.
 *
 * `GET /v1/auth/session` is the only source: `{ signedIn, tenantId, displayName, role }`, and the
 * shell renders from it — not from localStorage, which named a tenant ("acme") whatever the real
 * session was. A 401 from any later call flips the state to signed out, so an expired cookie shows
 * the sign-in screen instead of a page of raw errors.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError, UNAUTHENTICATED_EVENT } from "./api";

export type Session =
  | { signedIn: false }
  | { signedIn: true; tenantId: string; displayName: string; role: string };

interface SessionState {
  /** `null` until the first answer arrives. */
  session: Session | null;
  /** When the session could not be read at all (the API is down, not "not signed in"). */
  problem: ApiError | null;
  refresh: () => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    api<Session>("/v1/auth/session")
      .then((s) => { if (live) { setSession(s); setProblem(null); } })
      .catch((e: unknown) => {
        if (!live) return;
        // A 401 here is a key that was not accepted, which the SPA never sends; anything else is
        // the API being unreachable. Either way there is no session to render.
        setSession({ signedIn: false });
        setProblem(e instanceof ApiError ? e : new ApiError(0, String(e)));
      });
    return () => { live = false; };
  }, [nonce]);

  useEffect(() => {
    const onUnauthenticated = () => setSession({ signedIn: false });
    window.addEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, []);

  const signOut = useCallback(async () => {
    // `api()` always sends `content-type: application/json`, and Fastify refuses a JSON request with
    // an EMPTY body as a 400 before the handler runs — so sign-out never happened in a real browser
    // until the end-to-end suite clicked the button (2026-10-14). The body is an empty object.
    await api("/v1/auth/logout", { method: "POST", body: "{}" });
    refresh();
  }, [refresh]);

  return <Ctx.Provider value={{ session, problem, refresh, signOut }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession must be used under SessionProvider");
  return v;
}
